import { supabase } from '../lib/supabaseClient';
import { getEnv } from '../utils/env';

export interface MessageRecord extends ContactMessage {
  id: string;
  status: string;
  created_at?: string;
}

export interface SubscriberRecord {
  id: string;
  email: string;
  status: string;
  subscribed_at?: string;
}

export interface ContactMessage {
  name: string;
  email: string;
  subject: string;
  message: string;
}

/**
 * Service for handling contact form messages and newsletter subscriptions.
 * Provides methods to submit messages and manage newsletter subscriptions.
 * All methods throw on error.
 */

export const contactService = {
  /**
   * Submit a contact message.
   * @param {Object} messageData - The message data.
   * @param {string} messageData.name - Sender's name.
   * @param {string} messageData.email - Sender's email.
   * @param {string} messageData.subject - Message subject.
   * @param {string} messageData.message - Message body.
   * @returns {Promise<unknown>} Inserted message data.
   * @throws {Error} If validation fails or database error occurs.
   */

  async submitMessage(
    messageData: ContactMessage,
  ): Promise<MessageRecord[]> {
    // Runtime validation
    if (
      !messageData ||
      typeof messageData.name !== 'string' || !messageData.name.trim() ||
      typeof messageData.email !== 'string' || !messageData.email.trim() ||
      typeof messageData.subject !== 'string' || !messageData.subject.trim() ||
      typeof messageData.message !== 'string' || !messageData.message.trim()
    ) {
      throw new Error('All message fields are required');
    }
    const { data, error } = await supabase
      .from('messages')
      .insert([
        {
          name: messageData.name,
          email: messageData.email,
          subject: messageData.subject,
          message: messageData.message,
          status: 'new'
        }
      ]);
    if (error) throw error;
    console.info('Message submitted:', data);
    return (data ?? []) as MessageRecord[];
  },

  /**
   * Subscribe an email address to the newsletter.
   * @param {string} email - The email address to subscribe.
   * @returns {Promise<unknown>} Upserted subscriber data.
   * @throws {Error} If email is invalid or already subscribed or on database error.
   */

  async subscribeToNewsletter(email: string): Promise<SubscriberRecord[]> {
    // Simple email regex validation
    if (
      typeof email !== 'string' ||
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
    ) {
      throw new Error('Invalid email address');
    }

    console.log('📧 Starting newsletter subscription (double opt-in) for:', email);

    const workerUrl =
      getEnv('VITE_DATA_SYNC_WORKER_URL') ||
      'https://rocketcitynerd-data-sync.nchouser54.workers.dev';

    try {
      const response = await fetch(`${workerUrl}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      if (response.ok) {
        console.log('✅ Subscribe request accepted; confirmation email sent if new or pending');
        return [];
      }
      // Treat Conflict (already subscribed/pending) as success
      if (response.status === 409) {
        console.log('ℹ️ Already subscribed or pending confirmation; treating as success');
        return [];
      }
      const text = await response.text().catch(() => '');
      throw new Error(text || `Failed to subscribe: ${response.status}`);
    } catch (error) {
      console.error('💥 Newsletter subscription failed:', error);
      if (error instanceof Error) throw error;
      throw new Error('Unable to process subscription. Please try again later.');
    }
  },

  /**
   * Unsubscribe an email address from the newsletter.
   * @param {string} email - The email address to unsubscribe.
   * @returns {Promise<boolean>} True if successfully unsubscribed.
   * @throws {Error} If email is invalid or database error occurs.
   */
  async unsubscribeFromNewsletter(email: string): Promise<boolean> {
    // Simple email regex validation
    if (
      typeof email !== 'string' ||
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
    ) {
      throw new Error('Invalid email address');
    }

    console.log('🚫 Unsubscribing email:', email);

    try {
      // Prefer using the configured Supabase client; RLS policy permits anon updates for unsubscribe
      const { error } = await supabase
        .from('subscribers')
        .update({ status: 'unsubscribed', unsubscribed_at: new Date().toISOString() })
        .eq('email', email);

      if (error) {
        console.error('❌ Unsubscribe failed:', error);
        // Map "No rows" response to a friendlier message
        if (typeof error.message === 'string' && /No rows|0 rows/i.test(error.message)) {
          throw new Error('Email address not found in our subscribers list');
        }
        throw new Error('Failed to unsubscribe. Please try again.');
      }

      console.log('✅ Successfully unsubscribed:', email);
      return true;
    } catch (error) {
      console.error('💥 Unsubscribe error:', error);
      if (error instanceof Error) throw error;
      throw new Error('Failed to unsubscribe. Please try again.');
    }
  }
};
